import React from "react";
import { createStyles, Theme, WithStyles, withStyles } from "@material-ui/core";
import "brace/snippets/html";
import "brace/snippets/text";
import "brace/mode/text";
import "brace/ext/language_tools";
import debounce from "lodash/debounce";
import Button from "@material-ui/core/Button";
import {
  CompleterInterface,
  Editor,
  I18n,
  OneDataSource
} from "../../../index";
import { TitleRow } from "../TitleRow";
import {
  DefaultContentContainer,
  DefaultTitleContainer,
  DefaultViewContainer,
  ViewProps
} from "../OneView";
import { inject, observer } from "mobx-react";

const styles = (theme: Theme) =>
  createStyles({
    editor: {
      border: "1px solid rgb(189, 189, 189)",
      borderRadius: "4px",
      marginRight: theme.spacing(4),
      marginLeft: theme.spacing(4)
    },
    title: {},
    titleButtons: {},
    submitButton: {
      float: "right",
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(3),
      width: "200px"
    }
  });

interface ValidationProps {
  validationDelay: number;
  validationFunction: (data: any) => Promise<boolean>;
}

export interface EditorViewProps<T, ID>
  extends ViewProps,
    WithStyles<typeof styles> {
  dataSource: OneDataSource<T, ID>;
  completer?: CompleterInterface;
  field: string;
  id: ID;
  i18n?: I18n;
  name: string;
  mode?: string;
  validation?: ValidationProps;
}

export interface EditorViewState {
  oldData: any;
  newData: any;
  invalid: boolean;
}

@inject("i18n")
@observer
class EditorViewComponent<T, ID> extends React.Component<
  EditorViewProps<T, ID>,
  EditorViewState
> {
  private readonly debounceValidation: any = null;

  public constructor(props: EditorViewProps<T, ID>) {
    super(props);
    const oldData = "";
    const newData = "";
    const invalid = true;
    this.state = { oldData, newData, invalid };

    if (this.props.validation) {
      this.debounceValidation = debounce(
        this.validate,
        this.props.validation.validationDelay
      );
    }
  }

  public componentDidMount() {
    this.props.dataSource.clear();
    this.load().then(result => {
      const res = result as any;
      this.setState({
        oldData: res[this.props.field],
        newData: res[this.props.field]
      });
    });
  }

  public async load() {
    return this.props.dataSource.findOne(this.props.id);
  }

  public validate() {
    if (this.props.validation) {
      this.props.validation
        .validationFunction(this.state.newData)
        .then((response: boolean) => {
          this.setState({ invalid: !response });
        });
    }
  }

  public render() {
    const props = this.props as any;
    const i18n = this.props?.i18n as I18n;

    const ViewContainer = this.props.viewContainer || DefaultViewContainer;
    const TitleContainer = this.props.titleContainer || DefaultTitleContainer;
    const ContentContainer =
      this.props.contentContainer || DefaultContentContainer;

    return (
      <ViewContainer>
        <TitleContainer>
          <TitleRow title={props.title} />
        </TitleContainer>
        <ContentContainer>
          <Editor
            name={props.name}
            mode={props.mode}
            value={this.state.newData}
            completer={props.completer}
            editorStyle={props.classes.editor}
            onChange={this.changed}
          />

          <Button
            variant="contained"
            color="primary"
            disabled={this.state.invalid}
            className={props.classes.submitButton}
            onClick={this.save}
          >
            {i18n.t("commons:save", "Save")}
          </Button>
        </ContentContainer>
      </ViewContainer>
    );
  }

  private changed = (value: string, event?: any) => {
    this.setState({ newData: value });
    if (value === this.state.oldData) {
      this.setState({ invalid: true });
    } else if (this.debounceValidation != null) {
      this.debounceValidation();
    } else {
      this.setState({ invalid: false });
    }
  };

  private save = () => {
    const dto: any = {};
    dto[this.props.field] = this.state.newData;
    this.props.dataSource
      .partialUpdate(dto)
      .then(() => {
        this.setState({ oldData: this.state.newData, invalid: true });
        this.load();
      })
      .catch((error: any) => {
        this.setState({ newData: this.state.oldData, invalid: true });
      });
  };
}
export const EditorView = withStyles(styles, { withTheme: true })(
  EditorViewComponent
);
